// client/src/lib/creditNoteApi.ts
//
// Adapter over the Credit Note backend. The backend model is richer (itemName +
// itemDescription, clientPoNumber, per-item discountPercent, currency, subtotal/vatTotal/
// grandTotal) and wraps every response in { success, data }. The UI here speaks a leaner,
// shape, so this module translates in BOTH directions — the pages never see the
// server shape. D96.17: a note is in its invoice's currency; one with no invoice states TTD or USD.
import { get, patch, postJson, put } from './apiClient';
import { InvoicePayer, payerOf } from './clientInvoiceApi';
import { fetchServerPrint } from './serverPrint';

export type CreditNoteType = 'item_return' | 'discount' | 'correction';

/**
 * D44 #7 (VR-4) — where an Item Return's goods went, asked once per note. An
 * Item Return cannot be issued until it is answered
 * (CREDIT_NOTE_GOODS_DESTINATION_REQUIRED). Discount and Correction have none.
 */
export type GoodsDestination = 'united_stock' | 'vendor' | 'not_returned';

export const GOODS_DESTINATION_LABELS: Record<GoodsDestination, string> = {
  united_stock: 'Back into United stock',
  vendor: 'Straight to the vendor',
  not_returned: 'Not returned',
};

/**
 * One batch a credit note line's goods go back into (D44 #8, #36-#38, #42). On a
 * draft only the first four fields are written: the shipment and batch chosen,
 * and how many of the batch's own unit (or which weighed boxes) go back. Issue
 * fills in where they went; Cancel fills in what came back out.
 */
export interface StockReturnEntry {
  shipmentMovementId: string;
  layerId: string;
  /** In the batch's unit. 0 on a draft that ticks boxes; their weight once issued. */
  qtyLevel1: number;
  caseSeqs: number[];
  returnMovementId?: string | null;
  /** The batch they went into: the same one, or a new United batch for Pantry goods. */
  returnLayerId?: string | null;
  returnCaseSeqs?: number[];
  takenBackLevel1?: number | null;
  takenBackCaseSeqs?: number[];
  takebackMovementId?: string | null;
}

/** On Cancel: goods that had already left their batch and could not be taken back out (#39). */
export interface StockReturnShortfall {
  lineNumber: number | null;
  itemName: string;
  batchLabel: string;
  qtyLevel1: number;
  unit: string;
  caseSeqs: number[];
}

/** What Issue and Cancel did to stock, for the note as a whole. */
export interface CreditNoteStockReturnRecord {
  returnedAt: string | null;
  takenBackAt: string | null;
  shortfall: StockReturnShortfall[];
  supplierReturnsRaisedAt: string | null;
}

export interface CreditNoteItem {
  lineNumber?: number;
  quantity: number;       // negative for returns/corrections
  uom: string;
  description: string;
  unitPrice: number;      // positive
  extensionPrice: number; // negative
  /** Which line of the source invoice this credits. The server caps the row against it. */
  sourceInvoiceLineNumber?: number | null;
  /** Item Return, "Back into United stock": the batches this line's goods go into. */
  stockReturn?: StockReturnEntry[];
}

// ---- where an invoice's goods can go back (VR-4) ---------------------------

/** A weighed box that went to the vessel whole and has not come back. */
export interface ReturnSourceBox {
  seq: number;
  code: string;
  weightLevel1: number;
}

/**
 * One shipment of an invoice line (a delivery slip, or a vendor-invoice split to
 * this vessel) and one batch it drew from. Every quantity is in `unitLevel1`, the
 * batch's own unit, and worked out by the server.
 */
export interface ReturnSource {
  /** `${shipmentMovementId}:${layerId}` */
  key: string;
  kind: 'delivery_slip' | 'vendor_split';
  shipmentMovementId: string;
  layerId: string;
  shipmentLabel: string;
  shippedAt: string | null;
  vesselName: string;
  itemId: string;
  itemName: string;
  location: 'united' | 'pantry';
  batchLabel: string;
  expiryDate: string | null;
  unitLevel1: string;
  sentLevel1: number;
  returnedLevel1: number;
  returnedOn: string[];
  returnableLevel1: number;
  /** Batch units per unit the client was billed; null when that cannot be told (#42). */
  level1PerClientUnit: number | null;
  /** Weighed boxes: the goods go back by ticking them (#37). */
  boxes: ReturnSourceBox[] | null;
  sourceVendorInvoiceId: string | null;
  sourceInvoiceNumber: string;
  sourceLineNumber: number | null;
  vendorReturnPossible: boolean;
}

export interface ReturnSourceLine {
  lineNumber: number;
  itemName: string;
  unit: string;
  quantity: number;
  sources: ReturnSource[];
}

export interface ReturnSources {
  clientInvoiceId: string;
  invoiceNumber: string;
  vesselName: string;
  lines: ReturnSourceLine[];
}

// ---- "Return to supplier?" (VR-4, D44 #9, #40-#44) --------------------------

export interface SupplierReturnRaisedRef {
  debitNoteId: string;
  debitNoteNumber: string;
  status: string;
}

/** A goods line the vendor return will carry. */
export interface SupplierReturnGoodsLine {
  lineNumber: number;
  description: string;
  unit: string;
  quantity: number | null;
  caseSeqs: number[];
  /** What stays in United stock because only whole billed units go back, e.g. "6 CAN stay in United stock". */
  stays: string;
}

/** A line that cannot go on the vendor return, and why. */
export interface SupplierReturnLeftOutLine {
  lineNumber: number;
  leftOut: string;
}

export interface SupplierReturnVendorInvoice {
  vendorInvoiceId: string;
  invoiceNumber: string;
  vendorName: string;
  currency: string | null;
  raised: SupplierReturnRaisedRef | null;
  unavailable: string | null;
  kind: 'goods';
  lines: Array<SupplierReturnGoodsLine | SupplierReturnLeftOutLine>;
}

export interface SupplierReturnOption {
  vendorInvoiceId: string;
  invoiceNumber: string;
  vendorName: string;
  currency: string | null;
  raised: SupplierReturnRaisedRef | null;
  unavailable: string | null;
  vendorLineNumber: number;
  description: string;
  unit: string;
  maxQuantity: number | null;
  suggestedQuantity: number | null;
  creditedInBilledUnits: number | null;
}

export interface SupplierReturnCreditLine {
  lineNumber: number;
  itemName: string;
  quantity: number;
  unit: string;
  options: SupplierReturnOption[];
}

export interface SupplierReturnOffer {
  creditNoteId: string;
  goodsDestination: GoodsDestination;
  kind: 'goods' | 'money_only';
  excluded: Array<{ lineNumber: number | null; message: string }>;
  vendorInvoices: SupplierReturnVendorInvoice[];
  creditLines: SupplierReturnCreditLine[];
}

export type RaiseSupplierReturnsBody =
  | { vendorInvoiceIds: string[] }
  | { splits: Array<{ creditLineNumber: number; vendorInvoiceId: string; vendorLineNumber: number; quantity: number }> };

export interface RaiseSupplierReturnsResult {
  raised: Array<{
    vendorInvoiceId: string;
    invoiceNumber: string;
    debitNoteId: string;
    debitNoteNumber: string;
    kind: 'goods' | 'money_only';
    leftOut: Array<{ lineNumber: number | null; message: string }>;
  }>;
  skipped: Array<{
    vendorInvoiceId: string;
    invoiceNumber: string;
    message: string;
    leftOut?: Array<{ lineNumber: number | null; message: string }>;
  }>;
}

export function isLeftOutLine(
  line: SupplierReturnGoodsLine | SupplierReturnLeftOutLine
): line is SupplierReturnLeftOutLine {
  return typeof (line as SupplierReturnLeftOutLine).leftOut === 'string';
}

/**
 * Whether the offer still has a vendor return that can be raised: a vendor invoice
 * not raised yet and not unavailable, with a line (goods) or an option (money only).
 */
export function hasRaisableSupplierReturn(offer: SupplierReturnOffer | null | undefined): boolean {
  if (!offer) return false;
  const open = (o: { raised: SupplierReturnRaisedRef | null; unavailable: string | null }) => !o.raised && !o.unavailable;
  if (offer.kind === 'goods') {
    return (offer.vendorInvoices || []).some((vi) => open(vi) && (vi.lines || []).some((l) => !isLeftOutLine(l)));
  }
  return (offer.creditLines || []).some((line) => (line.options || []).some(open));
}

/** The vendor returns already raised from the note that the offer names, once each. */
export function raisedReturnsOf(offer: SupplierReturnOffer | null | undefined): SupplierReturnRaisedRef[] {
  if (!offer) return [];
  const refs = [
    ...(offer.vendorInvoices || []).map((vi) => vi.raised),
    ...(offer.creditLines || []).flatMap((line) => (line.options || []).map((o) => o.raised)),
  ].filter((r): r is SupplierReturnRaisedRef => Boolean(r));
  const byId = new Map<string, SupplierReturnRaisedRef>();
  refs.forEach((r) => {
    if (!byId.has(r.debitNoteId)) byId.set(r.debitNoteId, r);
  });
  return Array.from(byId.values());
}

/** One line of the source invoice, with what is left to credit on it. */
export interface CreditableLine {
  lineNumber: number;
  itemName: string;
  itemDescription: string;
  uom: string;
  quantity: number;
  unitPrice: number | null;
  lineTotal: number;
  creditedQuantity: number;
  creditedAmount: number;
  remainingQuantity: number;
  remainingAmount: number;
}

export interface CreditableLines {
  invoiceId: string;
  invoiceNumber: string;
  clientName: string;
  vesselName: string;
  currency: string;
  invoiceTotal: number;
  creditedTotal: number;
  lines: CreditableLine[];
}

export interface CreditNote {
  _id?: string;
  creditNoteNumber?: string;
  type: CreditNoteType;
  category?: string;
  vesselName?: string;
  ownerName?: string;
  ownerAddress?: string;
  agentName?: string;
  /** D153.6 — who pays and the agent's address, copied from the invoice; none stated = the agent. */
  payer?: InvoicePayer;
  agentAddress?: string;
  clientName?: string;
  poNumber?: string;
  /**
   * D61 (USC-BUG-0027) — the day the CLERK chose, `yyyy-mm-dd`, '' when nobody
   * chose one. Not the date to display on its own: a reader falls back
   * `creditNoteDate || issuedAt || createdAt`, as the server's printed PDF and
   * the register do. Keeping it to the chosen day is what lets the form send it
   * straight back — a fallback round-tripped through Save would store a date
   * nobody picked, and `createdAt` past 20:00 Trinidad is tomorrow, which the
   * server refuses.
   */
  creditNoteDate?: string;
  linkedInvoices?: string[];
  linkedInvoiceNumbers?: string[];
  /** D119.14 — each linked invoice's total when the note was raised: how a no-line credit is split across several invoices. */
  linkedInvoiceTotals?: Array<number | null>;
  /** D96.17 — each linked invoice's currency as it is NOW ('' = none): a draft re-saves in it. */
  linkedInvoiceCurrencies?: string[];
  /** D96.17 — 'TTD' | 'USD' as stored; the server takes a linked invoice's and uses this only for a note with none. */
  currency?: string | null;
  items: CreditNoteItem[];
  grandTotal: number;
  discountPercent?: number | null;
  netInvoiceValue?: number | null;
  status?: string;
  notes?: string;
  createdAt?: string;
  /** D44 — why the credit is given: a key from the reason list, and its note. Internal only. */
  reasonKey?: string;
  reasonNote?: string;
  issuedAt?: string | null;
  cancelledAt?: string | null;
  /** D44 #7 — Item Return only; '' until answered. */
  goodsDestination?: GoodsDestination | '';
  /** D44 #8, #39 — what Issue and Cancel did to stock. null when the server sent none. */
  stockReturn?: CreditNoteStockReturnRecord | null;
}

/** D44 — Draft is editable; Issued is locked; Cancelled is final. */
export function isCreditNoteEditable(status: string | undefined): boolean {
  return String(status || 'draft') === 'draft';
}

/**
 * One row of the credit-note register (R2/R4).
 *
 * Unlike every other type in this file this one is NOT translated — the server
 * builds the register row for the register, so its shape is already the shape
 * the screen shows, and a translation layer here would only be somewhere for the
 * two column sets to drift apart. `alert` and `daysOld` are computed server-side
 * by `utils/registerAlert.js`, the same owner the invoice register uses; the page
 * renders that flag and never recomputes it.
 */
export interface CreditNoteRegisterRow {
  id: string;
  creditNoteNumber: string;
  type: CreditNoteType;
  clerkName?: string;
  date?: string;
  vessel?: string;
  ownerName?: string;
  agentName?: string;
  clientPoNumber?: string;
  linkedInvoiceNumbers?: string;
  grandTotal: number;
  currency?: string;
  emailed: boolean;
  emailedTo?: string;
  emailedAt?: string | null;
  alert: boolean;
  daysOld: number | null;
  remarks?: string;
  status?: string;
}

export interface CreditNoteRegisterFilters {
  from?: string;
  to?: string;
  vessel?: string;
  status?: string;
  alertOnly?: boolean;
}

/** The working behind a discount: each invoice, each prior credit, and the resulting net. */
export interface NetValueBreakdown {
  netInvoiceValue: number;
  invoices: Array<{ number: string; amount: number }>;
  priorCredits: Array<{ number: string; amount: number }>;
  /** Server-rendered description — exactly what will print on the note. */
  description: string;
}

// ---- shape translation -----------------------------------------------------

const GOODS_DESTINATIONS: GoodsDestination[] = ['united_stock', 'vendor', 'not_returned'];

function idOrNull(value: unknown): string | null {
  return value === null || value === undefined || value === '' ? null : String(value);
}

function seqsOf(value: unknown): number[] {
  return Array.isArray(value) ? value.map(Number).filter((n) => Number.isInteger(n)) : [];
}

function numberOrNull(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function stockReturnEntryFromServer(e: any): StockReturnEntry {
  return {
    shipmentMovementId: String(e?.shipmentMovementId || ''),
    layerId: String(e?.layerId || ''),
    qtyLevel1: Number(e?.qtyLevel1) || 0,
    caseSeqs: seqsOf(e?.caseSeqs),
    returnMovementId: idOrNull(e?.returnMovementId),
    returnLayerId: idOrNull(e?.returnLayerId),
    returnCaseSeqs: seqsOf(e?.returnCaseSeqs),
    takenBackLevel1: numberOrNull(e?.takenBackLevel1),
    takenBackCaseSeqs: seqsOf(e?.takenBackCaseSeqs),
    takebackMovementId: idOrNull(e?.takebackMovementId),
  };
}

function stockReturnRecordFromServer(sr: any): CreditNoteStockReturnRecord | null {
  if (!sr || typeof sr !== 'object') return null;
  return {
    returnedAt: sr.returnedAt || null,
    takenBackAt: sr.takenBackAt || null,
    shortfall: (Array.isArray(sr.shortfall) ? sr.shortfall : []).map((s: any) => ({
      lineNumber: numberOrNull(s?.lineNumber),
      itemName: s?.itemName || '',
      batchLabel: s?.batchLabel || '',
      qtyLevel1: Number(s?.qtyLevel1) || 0,
      unit: s?.unit || '',
      caseSeqs: seqsOf(s?.caseSeqs),
    })),
    supplierReturnsRaisedAt: sr.supplierReturnsRaisedAt || null,
  };
}

/** Server credit note → the UI's leaner shape. */
function fromServer(cn: any): CreditNote {
  const links: any[] = Array.isArray(cn?.linkedInvoices) ? cn.linkedInvoices : [];
  const items: any[] = Array.isArray(cn?.items) ? cn.items : [];
  return {
    _id: cn?._id,
    creditNoteNumber: cn?.creditNoteNumber || '',
    type: cn?.type,
    category: cn?.category || '',
    vesselName: cn?.vesselName || '',
    ownerName: cn?.ownerName || '',
    ownerAddress: cn?.ownerAddress || '',
    agentName: cn?.agentName || '',
    payer: payerOf(cn),
    agentAddress: cn?.agentAddress || '',
    clientName: cn?.clientName || '',
    poNumber: cn?.clientPoNumber || '',
    creditNoteDate: String(cn?.creditNoteDate || '').slice(0, 10),
    linkedInvoices: links.map((li) => String(li?.clientInvoiceId || '')).filter(Boolean),
    linkedInvoiceNumbers: links.map((li) => li?.invoiceNumber || ''),
    linkedInvoiceTotals: links.map((li) => numberOrNull(li?.invoiceTotal)),
    linkedInvoiceCurrencies: links.map((li) => li?.invoiceCurrency || ''),
    currency: cn?.currency || null,
    items: items.map((it) => ({
      lineNumber: it?.lineNumber,
      quantity: Number(it?.quantity) || 0,
      uom: it?.uom || '',
      description: it?.itemName || it?.itemDescription || '',
      unitPrice: Number(it?.unitPrice) || 0,
      extensionPrice: Number(it?.extension) || 0,
      sourceInvoiceLineNumber:
        it?.sourceInvoiceLineNumber === null || it?.sourceInvoiceLineNumber === undefined
          ? null
          : Number(it.sourceInvoiceLineNumber),
      stockReturn: (Array.isArray(it?.stockReturn) ? it.stockReturn : []).map(stockReturnEntryFromServer),
    })),
    grandTotal: Number(cn?.grandTotal) || 0,
    discountPercent: Number(items[0]?.discountPercent) || 0,
    netInvoiceValue: null,
    status: cn?.status || 'draft',
    notes: cn?.remarks || '',
    createdAt: cn?.createdAt,
    reasonKey: cn?.reasonKey || '',
    reasonNote: cn?.reasonNote || '',
    issuedAt: cn?.issuedAt || null,
    cancelledAt: cn?.cancelledAt || null,
    goodsDestination: GOODS_DESTINATIONS.includes(cn?.goodsDestination) ? cn.goodsDestination : '',
    stockReturn: stockReturnRecordFromServer(cn?.stockReturn),
  };
}

/** UI payload → server create/update body. */
function toServer(payload: Partial<CreditNote>): any {
  const body: any = {
    type: payload.type,
    clientName: payload.clientName || '',
    vesselName: payload.vesselName || '',
    ownerName: payload.ownerName || '',
    ownerAddress: payload.ownerAddress || '',
    agentName: payload.agentName || '',
    payer: payerOf(payload),
    agentAddress: payload.agentAddress || '',
    clientPoNumber: payload.poNumber || '',
    // D61 — always sent, blank included: a blank IS "no date", and the server
    // then falls back to the issue timestamp as it always did.
    creditNoteDate: payload.creditNoteDate || '',
    category: payload.category || '',
    remarks: payload.notes || '',
    clientInvoiceIds: payload.linkedInvoices || [],
    currency: payload.currency || null,
    reasonKey: payload.reasonKey || '',
    reasonNote: payload.reasonNote || '',
  };
  // D44 #7 — every save of an Item Return says where its goods went: the server
  // reads a missing answer as unanswered, so leaving it off would wipe the choice.
  if (payload.type === 'item_return') body.goodsDestination = payload.goodsDestination || '';
  // Only "Back into United stock" carries a split across batches.
  const carriesSplit = payload.type === 'item_return' && payload.goodsDestination === 'united_stock';
  if (payload.type === 'discount') {
    // The backend synthesises the single discount line from percent + linked invoices.
    body.percent = Number(payload.discountPercent) || 0;
  } else {
    body.items = (payload.items || []).map((it, idx) => ({
      lineNumber: it.lineNumber ?? idx + 1,
      itemName: it.description || '',
      itemDescription: '',
      quantity: Number(it.quantity) || 0,
      uom: it.uom || '',
      unitPrice: Number(it.unitPrice) || 0,
      // The server caps the row against this invoice line; dropping it here would
      // silently turn a capped row back into a free one.
      sourceInvoiceLineNumber: it.sourceInvoiceLineNumber ?? null,
      ...(carriesSplit
        ? {
            stockReturn: (it.stockReturn || []).map((e) => ({
              shipmentMovementId: e.shipmentMovementId,
              layerId: e.layerId,
              qtyLevel1: e.qtyLevel1,
              caseSeqs: e.caseSeqs || [],
            })),
          }
        : {}),
    }));
  }
  return body;
}

// ---- API -------------------------------------------------------------------

export async function listCreditNotes(
  params: { page?: number; limit?: number; search?: string; type?: string } = {}
): Promise<{ data: CreditNote[]; total: number; page: number; limit: number }> {
  const q = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v != null && v !== '') q.set(k, String(v));
  });
  const r = await get<{ success: boolean; data: { creditNotes: any[]; pagination: any } }>(
    `/credit-notes?${q.toString()}`
  );
  const rows = r.data?.creditNotes || [];
  const pg = r.data?.pagination || {};
  return {
    data: rows.map(fromServer),
    total: Number(pg.total) || rows.length,
    page: Number(pg.page) || 1,
    limit: Number(pg.limit) || rows.length,
  };
}

export async function getCreditNote(id: string): Promise<CreditNote> {
  const r = await get<{ success: boolean; data: any }>(`/credit-notes/${id}`);
  return fromServer(r.data);
}

/** The saved credit note printed in the tax-invoice frame, as a PDF (D157.16). */
export const fetchCreditNotePdf = (id: string) => fetchServerPrint(`/credit-notes/${encodeURIComponent(id)}/pdf`);

export async function createCreditNote(payload: Partial<CreditNote>): Promise<CreditNote> {
  const r = await postJson<{ success: boolean; data: any }>('/credit-notes', toServer(payload));
  return fromServer(r.data);
}

export async function updateCreditNote(id: string, payload: Partial<CreditNote>): Promise<CreditNote> {
  const r = await put<{ success: boolean; data: any }>(`/credit-notes/${id}`, toServer(payload));
  return fromServer(r.data);
}

/**
 * D44 — Issue a draft: it becomes the client's credit and is locked. The server
 * re-checks the credit caps and refuses a note without a reason
 * (CREDIT_NOTE_REASON_REQUIRED) or one changed since it was read (CREDIT_NOTE_CHANGED).
 */
export async function issueCreditNote(id: string): Promise<CreditNote> {
  const r = await postJson<{ success: boolean; data: any }>(`/credit-notes/${id}/issue`, {});
  return fromServer(r.data);
}

/** D44 — Cancel a draft or issued note. It stops counting against the invoice. */
export async function cancelCreditNote(id: string): Promise<CreditNote> {
  const r = await postJson<{ success: boolean; data: any }>(`/credit-notes/${id}/cancel`, {});
  return fromServer(r.data);
}

/** Credit notes raised against a client invoice + the total credited (negative). CN-8. */
export async function getCreditNotesForInvoice(
  invoiceId: string
): Promise<{ creditNotes: CreditNote[]; totalCredited: number }> {
  const r = await get<{ success: boolean; creditNotes: any[]; totalCredited: number }>(
    `/credit-notes/for-invoice/${invoiceId}`
  );
  return {
    creditNotes: (r.creditNotes || []).map(fromServer),
    totalCredited: Number(r.totalCredited) || 0,
  };
}

/**
 * The source invoice's lines, each with how much of it is still creditable.
 *
 * `excludeCreditNoteId` is required when EDITING a note: its own lines are among
 * that invoice's credits, and counting them would show every line as already
 * fully credited by the note you are looking at.
 */
export async function getCreditableLines(
  invoiceId: string,
  excludeCreditNoteId?: string
): Promise<CreditableLines> {
  const q = excludeCreditNoteId ? `?excludeCreditNoteId=${encodeURIComponent(excludeCreditNoteId)}` : '';
  const r = await get<{ success: boolean; data: CreditableLines }>(`/credit-notes/creditable/${invoiceId}${q}`);
  return r.data;
}

/**
 * D44 #8 (VR-4) — for each line of a client invoice, the shipments and batches its
 * goods left on for the vessel, and how much of each can still go back into
 * United stock. `excludeCreditNoteId` when editing a note, as with the creditable lines.
 */
export async function getReturnSources(clientInvoiceId: string, excludeCreditNoteId?: string): Promise<ReturnSources> {
  const q = new URLSearchParams({ clientInvoiceId });
  if (excludeCreditNoteId) q.set('excludeCreditNoteId', excludeCreditNoteId);
  const r = await get<{ success: boolean; data: ReturnSources }>(`/credit-notes/return-sources?${q.toString()}`);
  return r.data;
}

/**
 * D44 #9 (VR-4) — what "Return to supplier?" offers for an issued Item Return.
 * Refused with CN_SUPPLIER_RETURN_NOT_AVAILABLE for any other note.
 */
export async function getSupplierReturnOffer(creditNoteId: string): Promise<SupplierReturnOffer> {
  const r = await get<{ success: boolean; data: SupplierReturnOffer }>(`/credit-notes/${creditNoteId}/supplier-return`);
  return r.data;
}

/** Raise the vendor returns chosen, as DRAFTS: goods by vendor invoice, money only by split. */
export async function raiseSupplierReturns(
  creditNoteId: string,
  body: RaiseSupplierReturnsBody
): Promise<RaiseSupplierReturnsResult> {
  const r = await postJson<{ success: boolean; data: RaiseSupplierReturnsResult }>(
    `/credit-notes/${creditNoteId}/supplier-return`,
    body
  );
  return r.data;
}

// ---- register (R2/R4/R5/R8) ------------------------------------------------

function registerQuery(filters: CreditNoteRegisterFilters = {}): string {
  const q = new URLSearchParams();
  if (filters.from) q.set('from', filters.from);
  if (filters.to) q.set('to', filters.to);
  if (filters.vessel) q.set('vessel', filters.vessel);
  if (filters.status && filters.status !== 'all') q.set('status', filters.status);
  if (filters.alertOnly) q.set('alertOnly', 'true');
  return q.toString();
}

/** The credit-note register — one row per note, with the emailed mark and the 1-week alert. */
export async function getCreditNoteRegister(
  filters: CreditNoteRegisterFilters = {}
): Promise<CreditNoteRegisterRow[]> {
  const r = await get<{ success: boolean; data: CreditNoteRegisterRow[] }>(
    `/credit-notes/register/credit-notes?${registerQuery(filters)}`
  );
  return r.data || [];
}

/**
 * Tick (or untick) the register's Emailed column. Ticking is what clears the
 * one-week alert — nothing else does — so unticking deliberately puts it back.
 */
export async function markCreditNoteEmailed(
  id: string,
  opts: { emailedTo?: string; emailed?: boolean } = {}
): Promise<void> {
  await patch<{ success: boolean; data: unknown }>(`/credit-notes/${id}/emailed`, {
    emailedTo: opts.emailedTo || '',
    emailed: opts.emailed !== false,
  });
}

/**
 * Download the register as CSV from the server's export endpoint rather than
 * serialising the loaded rows here. That endpoint is the one place the column
 * order is decided, and it escapes leading `=`/`+`/`@` so a vessel name out of
 * an emailed document cannot execute when the file is opened in Excel
 * (utils/csv.js, TECH_DEBT #38). It is behind the EXPORT permission, hence the
 * bearer token — a plain <a href> would arrive unauthenticated.
 */
export async function downloadCreditNoteRegisterCsv(
  filters: CreditNoteRegisterFilters = {}
): Promise<void> {
  const { API_BASE_URL } = await import('./apiClient');
  const { getAuthToken } = await import('./authSession');
  const token = getAuthToken();
  const res = await fetch(`${API_BASE_URL}/credit-notes/register/credit-notes/export.csv?${registerQuery(filters)}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) throw new Error('Export failed');
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `credit_note_register_${Date.now()}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/**
 * The next CR number, for display on a new draft. This only PEEKS — the real number is
 * issued atomically when the note is created, so two open drafts can never claim the same one.
 */
export async function getNextCreditNoteNumber(): Promise<string> {
  const r = await get<{ success: boolean; data: { creditNoteNumber: string } }>('/credit-notes/next-number');
  return r.data?.creditNoteNumber || '';
}

/**
 * Preview the net value a discount applies to. The net is the linked invoice totals MINUS
 * any prior credit notes against them; the percentage applies to that net, never the gross.
 * USC-BUG-0028 / D58 — the Category goes with it. Save scopes the discount to the chosen
 * category (an uncategorised invoice matching any of them); a preview that left it out was
 * computed by a different rule than the Save it previews, and could differ from it silently.
 */
export async function previewNetValue(
  linkedInvoices: string[],
  discountPercent = 0,
  category = ''
): Promise<NetValueBreakdown> {
  const r = await postJson<{
    success: boolean;
    data: {
      discountAmount: number; invoiceTotal: number; priorCredits: number; netBase: number; linkedInvoices: any[];
      lineName?: string; lineDescription?: string;
    };
  }>('/credit-notes/preview-discount', { clientInvoiceIds: linkedInvoices, percent: discountPercent, category });
  const d = r.data || ({} as any);
  const invoices = (d.linkedInvoices || []).map((li: any) => ({
    number: li.invoiceNumber || '',
    amount: Number(li.invoiceTotal) || 0,
  }));
  return {
    netInvoiceValue: Number(d.netBase) || 0,
    invoices,
    priorCredits: [],
    // D157.16: the server's own line text, the one the note stores and prints.
    description: [d.lineName, d.lineDescription].filter(Boolean).join(' '),
  };
}
